import React, { useEffect, useState } from "react";
import { Container, Stack, Typography, CircularProgress, Box, Tabs, Tab, Chip, Button } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { GetAllJobsAsync } from "../../services/services";
import { asyncStatus } from "../../utils/asyncStatus";
import { themeOrange } from "../../utils/colorTheme";
import { useNavigate } from "react-router-dom";
import CompletedJobsTab from "../../component/MyJobTabs/CompletedJobTabs/CompletedJobsTab";
import ActiveJobsTab from "../../component/MyJobTabs/ActiveJobsTab/ActiveJobsTab";
import WaitingJobsTab from "../../component/MyJobTabs/WaitingJobTabs/WaitingJobsTab";
import { setJobIdleStatus } from "../../store/slices/services_slice";
import { FiCheckCircle, FiClock, FiAlertCircle, FiBriefcase } from "react-icons/fi";

const MyJobs = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  const { get_all_jobs_status, get_all_jobs_data, update_jobs_status } = useSelector(
    (state) => state.serviceManage
  );

  const jobLoader = get_all_jobs_status === asyncStatus.LOADING;

  // Count jobs by status
  const jobCounts = {
    active: get_all_jobs_data?.filter(job =>
      job.status === "Assigned" || job.status === "In-Progress"
    )?.length || 0,
    completed: get_all_jobs_data?.filter(job => job.status === "Completed")?.length || 0,
    waiting: get_all_jobs_data?.filter(job => {
      const s = job.status?.trim().toLowerCase();
      return s === "in-queue" || s === "pending";
    })?.length || 0,
  };

  const tabs = [
    {
      label: "Active",
      icon: <FiClock size={18} />,
      count: jobCounts.active,
      content: <ActiveJobsTab jobLoader={jobLoader} />
    },
    {
      label: "Completed",
      icon: <FiCheckCircle size={18} />,
      count: jobCounts.completed,
      content: <CompletedJobsTab jobLoader={jobLoader} />,
    },
    {
      label: "Pro Approval",
      icon: <FiAlertCircle size={18} />,
      count: jobCounts.waiting,
      content: <WaitingJobsTab jobLoader={jobLoader} />,
    },
  ];

  useEffect(() => {
    dispatch(GetAllJobsAsync());
  }, []);

  useEffect(() => {
    if (update_jobs_status === asyncStatus.SUCCEEDED) {
      dispatch(GetAllJobsAsync());
      dispatch(setJobIdleStatus());
    }
  }, [update_jobs_status]);

  return (
    <Box sx={{ backgroundColor: "#f8fafc", minHeight: "100vh", pb: 4 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          sx={{ pt: 4, pb: 3 }}
          gap={2}
        >
          <Stack>
            <Typography
              sx={{
                fontSize: { xs: "24px", md: "28px" },
                fontWeight: 700,
                color: "#1a1a1a",
              }}
            >
              My Jobs
            </Typography>
            <Typography sx={{ fontSize: "14px", color: "#6b7280" }}>
              Track and manage all your service requests
            </Typography>
          </Stack>

          <Button
            onClick={() => navigate("/dashboard/services")}
            variant="contained"
            sx={{
              background: "linear-gradient(135deg, #F15A24 0%, #d94d1a 100%)",
              borderRadius: "12px",
              textTransform: "none",
              px: 3,
              py: 1.5,
              fontSize: "14px",
              fontWeight: 600,
              boxShadow: "0 4px 14px rgba(241, 90, 36, 0.3)",
              "&:hover": {
                background: "linear-gradient(135deg, #d94d1a 0%, #c53f10 100%)",
                boxShadow: "0 6px 20px rgba(241, 90, 36, 0.4)",
              },
            }}
          >
            Book New Service
          </Button>
        </Stack>

        {/* Stats Cards */}
        <Stack direction="row" spacing={2} sx={{ mb: 4, overflowX: "auto", pb: 1 }}>
          <Box
            sx={{
              flex: "0 0 auto",
              minWidth: "140px",
              background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
              borderRadius: "16px",
              p: 2.5,
              color: "white",
            }}
          >
            <FiBriefcase size={24} style={{ opacity: 0.8 }} />
            <Typography sx={{ fontSize: "28px", fontWeight: 700, mt: 1 }}>
              {get_all_jobs_data?.length || 0}
            </Typography>
            <Typography sx={{ fontSize: "13px", opacity: 0.8 }}>Total Jobs</Typography>
          </Box>
          <Box
            sx={{
              flex: "0 0 auto",
              minWidth: "140px",
              background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
              borderRadius: "16px",
              p: 2.5,
              color: "white",
            }}
          >
            <FiClock size={24} style={{ opacity: 0.8 }} />
            <Typography sx={{ fontSize: "28px", fontWeight: 700, mt: 1 }}>
              {jobCounts.active}
            </Typography>
            <Typography sx={{ fontSize: "13px", opacity: 0.8 }}>Active</Typography>
          </Box>
          <Box
            sx={{
              flex: "0 0 auto",
              minWidth: "140px",
              background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
              borderRadius: "16px",
              p: 2.5,
              color: "white",
            }}
          >
            <FiCheckCircle size={24} style={{ opacity: 0.8 }} />
            <Typography sx={{ fontSize: "28px", fontWeight: 700, mt: 1 }}>
              {jobCounts.completed}
            </Typography>
            <Typography sx={{ fontSize: "13px", opacity: 0.8 }}>Completed</Typography>
          </Box>
          <Box
            sx={{
              flex: "0 0 auto",
              minWidth: "140px",
              background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
              borderRadius: "16px",
              p: 2.5,
              color: "white",
            }}
          >
            <FiAlertCircle size={24} style={{ opacity: 0.8 }} />
            <Typography sx={{ fontSize: "28px", fontWeight: 700, mt: 1 }}>
              {jobCounts.waiting}
            </Typography>
            <Typography sx={{ fontSize: "13px", opacity: 0.8 }}>Pending</Typography>
          </Box>
        </Stack>

        {/* Tabs Container */}
        <Box
          sx={{
            backgroundColor: "#fff",
            borderRadius: "20px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.06)",
            overflow: "hidden",
          }}
        >
          {/* Custom Tabs */}
          <Box sx={{ borderBottom: "1px solid #f1f1f1", px: 2 }}>
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              variant="scrollable"
              scrollButtons={false}
              allowScrollButtonsMobile
              sx={{
                "& .MuiTabs-indicator": {
                  backgroundColor: "#F15A24",
                  height: "3px",
                  borderRadius: "3px 3px 0 0",
                },
                "& .MuiTabs-scroller": {
                  overflow: "auto !important",
                },
              }}
            >
              {tabs.map((tab, index) => (
                <Tab
                  key={index}
                  label={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ color: activeTab === index ? "#F15A24" : "#6b7280" }}>
                        {tab.icon}
                      </Box>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: activeTab === index ? 600 : 500,
                          color: activeTab === index ? "#1a1a1a" : "#6b7280",
                          textTransform: "none",
                        }}
                      >
                        {tab.label}
                      </Typography>
                      {tab.count > 0 && (
                        <Chip
                          label={tab.count}
                          size="small"
                          sx={{
                            height: "20px",
                            fontSize: "11px",
                            fontWeight: 600,
                            backgroundColor: activeTab === index ? "#F15A24" : "#e5e7eb",
                            color: activeTab === index ? "white" : "#6b7280",
                          }}
                        />
                      )}
                    </Stack>
                  }
                  sx={{
                    minHeight: "56px",
                    px: 2,
                  }}
                />
              ))}
            </Tabs>
          </Box>

          {/* Tab Content */}
          <Box sx={{ p: { xs: 2, md: 3 }, minHeight: "400px" }}>
            {jobLoader ? (
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{ height: "300px" }}
              >
                <CircularProgress size={40} sx={{ color: themeOrange }} />
                <Typography sx={{ mt: 2, color: "#6b7280", fontSize: "14px" }}>
                  Loading your jobs...
                </Typography>
              </Stack>
            ) : get_all_jobs_data?.length === 0 ? (
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{ height: "300px" }}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    backgroundColor: "#f3f4f6",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 2,
                  }}
                >
                  <FiBriefcase size={32} color="#9ca3af" />
                </Box>
                <Typography sx={{ fontSize: "18px", fontWeight: 600, color: "#1a1a1a", mb: 1 }}>
                  No jobs yet
                </Typography>
                <Typography sx={{ fontSize: "14px", color: "#6b7280", mb: 3, textAlign: "center" }}>
                  Book your first service to get started
                </Typography>
                <Button
                  onClick={() => navigate("/dashboard/services")}
                  variant="contained"
                  sx={{
                    background: "linear-gradient(135deg, #F15A24 0%, #d94d1a 100%)",
                    borderRadius: "12px",
                    textTransform: "none",
                    px: 4,
                    py: 1.5,
                  }}
                >
                  Browse Services
                </Button>
              </Stack>
            ) : (
              tabs[activeTab].content
            )}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default MyJobs;
